import { Blog } from "../Model/Blog.model";
import sanitizeHtml from "sanitize-html";
import React from "react";
import generatedBlogs from "../data/generatedBlogs.json";
import blogContentEnhancements from "../data/blogContentEnhancements.json";
import { seoGuidePages } from "../data/seoGuidePages";
import { normalizeInternalHtmlLinks } from "../utils/routes";

type BlogContentEnhancement = Partial<Blog> & {
  appendBody?: string;
  bodySections?: string[];
  preserveInternalLinks?: boolean;
};

export interface BlogSectionLink {
  href: string;
  id: string;
  label: string;
}

const blogEnhancements = blogContentEnhancements as Record<
  string,
  BlogContentEnhancement
>;

function uniqueValues(values: string[]) {
  return Array.from(new Set(values.filter(Boolean)));
}

function applyBlogEnhancement(blog: Blog): Blog {
  const enhancement = blogEnhancements[blog.slug];
  const baseBody = normalizeInternalHtmlLinks(blog.body);

  if (!enhancement) {
    return {
      ...blog,
      body: baseBody,
    };
  }

  const {
    appendBody,
    bodySections,
    preserveInternalLinks,
    tags,
    relatedSlugs,
    ...blogOverrides
  } = enhancement;
  const enhancedBody =
    blogOverrides.body ||
    (bodySections ? bodySections.join("\n") : `${baseBody}${appendBody || ""}`);
  const resolvedBody = preserveInternalLinks
    ? enhancedBody
    : normalizeInternalHtmlLinks(enhancedBody);

  return {
    ...blog,
    ...blogOverrides,
    body: resolvedBody,
    tags: tags ? uniqueValues([...(blog.tags || []), ...tags]) : blog.tags,
    relatedSlugs: relatedSlugs || blog.relatedSlugs,
  };
}

export function getStaticBlogs(): Blog[] {
  const blogsBySlug = new Map<string, Blog>();

  [...(generatedBlogs as Blog[]), ...seoGuidePages].forEach(
    (blog) => {
      blogsBySlug.set(blog.slug, applyBlogEnhancement(blog));
    },
  );

  return Array.from(blogsBySlug.values());
}

export async function getAllBlogs(): Promise<Blog[]> {
  return getStaticBlogs();
}

export function getBlogRoutes() {
  return getStaticBlogs().map((blog) => ({
    path: blog.slug,
    label: blog.title,
  }));
}

export function getBlogByTitle(
  blogs: Blog[],
  title?: string
): Blog | undefined {
  if (title) {
    return blogs?.find(
      (blog: Blog) => blog.slug === title || encodeBlogTitle(blog.title) === title
    );
  }
  return undefined;
}

export function encodeBlogTitle(blogTitle: string) {
  if (blogTitle) {
    return blogTitle
      .toLowerCase()
      .trim()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
  return blogTitle;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function stripLeadingTitleHeading(htmlString: string, title?: string) {
  if (!htmlString || !title) {
    return htmlString;
  }

  const titlePattern = escapeRegExp(title).replace(/\s+/g, "\\s+");
  const leadingHeadingPattern = new RegExp(
    `^\\s*<h[1-4][^>]*>\\s*${titlePattern}\\s*<\\/h[1-4]>\\s*`,
    "i"
  );

  return htmlString.replace(leadingHeadingPattern, "");
}

function slugifyHeadingId(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function createUniqueHeadingId(baseLabel: string, usedIds: Set<string>) {
  const normalizedId = slugifyHeadingId(baseLabel) || "section";

  if (!usedIds.has(normalizedId)) {
    usedIds.add(normalizedId);
    return normalizedId;
  }

  let duplicateCount = 2;

  while (usedIds.has(`${normalizedId}-${duplicateCount}`)) {
    duplicateCount += 1;
  }

  const uniqueId = `${normalizedId}-${duplicateCount}`;
  usedIds.add(uniqueId);
  return uniqueId;
}

function stripIdAttribute(attributes: string) {
  return attributes.replace(/\s+id=(['"]).*?\1/gi, "");
}

export function getBlogContentSections(
  htmlString: string,
  title?: string,
  maxItems = 7,
) {
  const cleanedHtmlString = stripLeadingTitleHeading(htmlString, title);
  const usedIds = new Set<string>();
  const sectionLinks: BlogSectionLink[] = [];

  const contentHtml = cleanedHtmlString.replace(
    /<h2([^>]*)>([\s\S]*?)<\/h2>/gi,
    (match, attributes, innerHtml) => {
      const label = getPlainTextFromHtml(innerHtml);

      if (!label) {
        return match;
      }

      const id = createUniqueHeadingId(label, usedIds);

      if (sectionLinks.length < maxItems) {
        sectionLinks.push({
          href: `#${id}`,
          id,
          label,
        });
      }

      return `<h2${stripIdAttribute(attributes)} id="${id}">${innerHtml}</h2>`;
    },
  );

  return {
    contentHtml,
    sectionLinks,
  };
}

export function getPlainTextFromHtml(htmlString: string) {
  return sanitizeHtml(htmlString || "", {
    allowedTags: [],
    allowedAttributes: {},
    disallowedTagsMode: "discard",
  })
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function getReadingTimeMinutes(htmlString: string) {
  const wordCount = getPlainTextFromHtml(htmlString)
    .split(/\s+/)
    .filter(Boolean).length;

  return Math.max(1, Math.ceil(wordCount / 225));
}

export function parseHtml(htmlString: string, title?: string) {
  const cleanedHtmlString = stripLeadingTitleHeading(htmlString, title);
  return React.createElement("div", {
    dangerouslySetInnerHTML: {
      __html: sanitizeHtml(cleanedHtmlString, {
        allowedTags: [
          "p",
          "br",
          "strong",
          "em",
          "figure",
          "figcaption",
          "img",
          "ul",
          "ol",
          "li",
          "h2",
          "h3",
          "h4",
          "blockquote",
          "code",
          "pre",
          "a",
          "table",
          "thead",
          "tbody",
          "tr",
          "th",
          "td",
        ],
        allowedAttributes: {
          a: ["href", "target", "rel", "class"],
          img: ["src", "alt", "title", "width", "height", "loading"],
          h2: ["id"],
          h3: ["id"],
          h4: ["id"],
        },
        allowedSchemes: ["http", "https", "mailto"],
        disallowedTagsMode: "discard",
      }),
    },
  });
}
